import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { campaignAPI } from '@/lib/supabase/campaigns';
import { preferencesAPI } from '@/lib/supabase/preferences';
import { useCurrentTenantId } from '@/contexts/TenantContext';
import { logUserActivity } from '@/lib/activity/userActivity';
import { generateHumanDescription } from '@/lib/activity/humanDescriptions';

export interface Campaign {
  campaign_number: string;
  recall_title: string;
  total_recalls: number;
  skillLevel?: number | null;
  mobile_servicing_eligible?: boolean;
  has_advance_notice_recalls?: boolean;
}

export interface CampaignPreferences {
  skillLevels: Record<string, number | null>;
  mobileEligibility: Record<string, boolean>;
  campaignStatus: Record<string, 'Available' | 'Advance Notice' | 'Paused' | null>;
  campaignStatusReasons: Record<string, string | null>;
}

/**
 * Query function for campaigns data (exported for prefetching)
 */
export async function fetchCampaignsData(userId: string | undefined, tenantId: string | null) {
  if (!tenantId) {
    return { campaigns: [], preferences: null };
  }

  // Load campaigns (with dealer preferences if user is authenticated)
  const campaignsData = userId
    ? await campaignAPI.getCampaignsWithPreferences(userId)
    : await campaignAPI.getCampaigns();

  if (!userId) {
    return {
      campaigns: campaignsData,
      preferences: null,
      userSetFields: { status: new Set<string>() }
    };
  }

  // Load preferences from Supabase
  const preferences = await preferencesAPI.getCampaignPreferences(userId);

  // Skill Level + Mobile Eligible: tenant-wide last-writer-wins. Source from
  // campaignsData where deduplicateCampaigns has already picked the most-recent
  // writer's value across the tenant. The per-user preferences map is ignored
  // for these fields — there is no personal-override concept anymore.
  const skillLevels: Record<string, number | null> = {};
  const mobileEligibility: Record<string, boolean> = {};
  for (const c of campaignsData) {
    skillLevels[c.campaign_number] = c.skillLevel ?? null;
    mobileEligibility[c.campaign_number] = c.mobile_servicing_eligible ?? false;
  }

  // Status: keep the existing hybrid (per-user with Advance Notice MV fallback).
  const autoDetectedStatus: Record<string, 'Available' | 'Advance Notice' | 'Paused' | null> = {};
  campaignsData.forEach(campaign => {
    const campaignNumber = campaign.campaign_number;
    const userSetStatus = preferences.campaignStatus?.[campaignNumber];
    if (!userSetStatus && campaign.has_advance_notice_recalls) {
      autoDetectedStatus[campaignNumber] = 'Advance Notice';
    } else if (!userSetStatus) {
      autoDetectedStatus[campaignNumber] = 'Available';
    }
  });
  const mergedStatus = {
    ...autoDetectedStatus,
    ...(preferences.campaignStatus || {})
  };

  // Only Status retains "personally set" semantics (drives the ✎ marker).
  const userSetFields = {
    status: new Set(Object.keys(preferences.campaignStatus || {}))
  };

  return {
    campaigns: campaignsData,
    preferences: {
      skillLevels,
      mobileEligibility,
      campaignStatus: mergedStatus,
      campaignStatusReasons: preferences.campaignStatusReasons || {}
    },
    userSetFields
  };
}

/**
 * React Query hook to fetch campaigns with user preferences
 * Uses aggressive caching (10 min staleTime) since campaign data changes infrequently
 */
export function useCampaigns(userId?: string) {
  const tenantId = useCurrentTenantId();

  return useQuery({
    queryKey: ['campaigns', userId, tenantId],
    queryFn: () => fetchCampaignsData(userId, tenantId),
    enabled: !!tenantId,
    staleTime: 10 * 60 * 1000, // 10 minutes - campaigns change infrequently
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2
  });
}

/**
 * Mutation hook for updating campaign priority/skill level
 */
export function useUpdateCampaignPriority(userId?: string) {
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();

  return useMutation({
    mutationFn: async ({
      campaignNumber,
      priority
    }: {
      campaignNumber: string;
      priority: number | null;
    }) => {
      if (!userId) {
        throw new Error('User ID required');
      }
      const priorityValue = priority === null ? null : parseInt(priority.toString());
      await preferencesAPI.updateCampaignSkillLevel(userId, campaignNumber, priorityValue);
      return { campaignNumber, priority: priorityValue };
    },
    onMutate: async ({ campaignNumber, priority }) => {
      await queryClient.cancelQueries({ queryKey: ['campaigns', userId, tenantId] });

      const previousData = queryClient.getQueryData<ReturnType<typeof fetchCampaignsData>>([
        'campaigns',
        userId,
        tenantId
      ]);

      const priorityValue = priority === null ? null : parseInt(priority.toString());

      // Optimistically update the tenant-wide skill level. After refetch the
      // dedup in deduplicateCampaigns will reflect the new value as the most
      // recent writer for everyone in the tenant.
      if (previousData?.preferences) {
        queryClient.setQueryData(['campaigns', userId, tenantId], {
          ...previousData,
          preferences: {
            ...previousData.preferences,
            skillLevels: {
              ...previousData.preferences.skillLevels,
              [campaignNumber]: priorityValue
            }
          }
        });
      }

      // Capture old priority for audit log
      const oldPriority = previousData?.preferences?.skillLevels?.[campaignNumber] ?? null;
      return { previousData, oldPriority };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['campaigns', userId, tenantId], context.previousData);
      }
    },
    onSuccess: (_data, variables, context) => {
      const metadata = {
        campaign_number: variables.campaignNumber,
        priority: variables.priority,
        old_priority: context?.oldPriority
      };
      logUserActivity({
        category: 'campaign_config',
        action: 'update_campaign_priority',
        entityType: 'campaign',
        entityId: variables.campaignNumber,
        metadata: {
          ...metadata,
          human_description: generateHumanDescription(
            'campaign_config',
            'update_campaign_priority',
            metadata
          )
        }
      });
      queryClient.invalidateQueries({ queryKey: ['campaigns', userId, tenantId] });
    }
  });
}

/**
 * Mutation hook for updating mobile servicing eligibility
 */
export function useUpdateMobileEligibility(userId?: string) {
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();

  return useMutation({
    mutationFn: async ({
      campaignNumber,
      eligible
    }: {
      campaignNumber: string;
      eligible: boolean;
    }) => {
      if (!userId) {
        throw new Error('User ID required');
      }
      await preferencesAPI.updateCampaignMobileServicingEligibility(
        userId,
        campaignNumber,
        eligible
      );
      return { campaignNumber, eligible };
    },
    onMutate: async ({ campaignNumber, eligible }) => {
      await queryClient.cancelQueries({ queryKey: ['campaigns', userId, tenantId] });

      const previousData = queryClient.getQueryData<ReturnType<typeof fetchCampaignsData>>([
        'campaigns',
        userId,
        tenantId
      ]);

      // Optimistically update the tenant-wide mobile-eligible flag. Refetch
      // picks up the new last-writer value for everyone.
      if (previousData?.preferences) {
        queryClient.setQueryData(['campaigns', userId, tenantId], {
          ...previousData,
          preferences: {
            ...previousData.preferences,
            mobileEligibility: {
              ...previousData.preferences.mobileEligibility,
              [campaignNumber]: eligible
            }
          }
        });
      }

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['campaigns', userId, tenantId], context.previousData);
      }
    },
    onSuccess: (_data, variables) => {
      const metadata = {
        campaign_number: variables.campaignNumber,
        eligible: variables.eligible
      };
      logUserActivity({
        category: 'campaign_config',
        action: 'toggle_mobile_eligibility',
        entityType: 'campaign',
        entityId: variables.campaignNumber,
        metadata: {
          ...metadata,
          human_description: generateHumanDescription(
            'campaign_config',
            'toggle_mobile_eligibility',
            metadata
          )
        }
      });
      queryClient.invalidateQueries({ queryKey: ['campaigns', userId, tenantId] });
    }
  });
}

/**
 * Mutation hook for updating campaign status
 */
export function useUpdateCampaignStatus(userId?: string) {
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();

  return useMutation({
    mutationFn: async ({
      campaignNumber,
      status,
      statusReason
    }: {
      campaignNumber: string;
      status: 'Available' | 'Advance Notice' | 'Paused';
      statusReason?: string | null;
    }) => {
      if (!userId) {
        throw new Error('User ID required');
      }
      await preferencesAPI.updateCampaignStatus(userId, campaignNumber, status, statusReason);
      return { campaignNumber, status, statusReason };
    },
    onMutate: async ({ campaignNumber, status, statusReason }) => {
      await queryClient.cancelQueries({ queryKey: ['campaigns', userId, tenantId] });

      const previousData = queryClient.getQueryData<ReturnType<typeof fetchCampaignsData>>([
        'campaigns',
        userId,
        tenantId
      ]);

      if (previousData?.preferences) {
        queryClient.setQueryData(['campaigns', userId, tenantId], {
          ...previousData,
          preferences: {
            ...previousData.preferences,
            campaignStatus: {
              ...previousData.preferences.campaignStatus,
              [campaignNumber]: status
            },
            campaignStatusReasons: {
              ...previousData.preferences.campaignStatusReasons,
              [campaignNumber]: statusReason || null
            }
          },
          userSetFields: {
            ...previousData.userSetFields,
            status: new Set([...previousData.userSetFields.status, campaignNumber])
          }
        });
      }

      // Capture old status for audit log
      const oldStatus = previousData?.preferences?.campaignStatus?.[campaignNumber] ?? null;
      return { previousData, oldStatus };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['campaigns', userId, tenantId], context.previousData);
      }
    },
    onSuccess: (_data, variables, context) => {
      const metadata = {
        campaign_number: variables.campaignNumber,
        status: variables.status,
        old_status: context?.oldStatus,
        reason: variables.statusReason
      };
      logUserActivity({
        category: 'campaign_config',
        action: 'update_campaign_status',
        entityType: 'campaign',
        entityId: variables.campaignNumber,
        metadata: {
          ...metadata,
          human_description: generateHumanDescription(
            'campaign_config',
            'update_campaign_status',
            metadata
          )
        }
      });
      queryClient.invalidateQueries({ queryKey: ['campaigns', userId, tenantId] });
    }
  });
}

/**
 * Mutation hook for bulk resetting mobile servicing eligibility
 */
export function useResetAllMobileEligibility(userId?: string) {
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();

  return useMutation({
    mutationFn: async () => {
      if (!userId) {
        throw new Error('User ID required');
      }
      const data = queryClient.getQueryData<ReturnType<typeof fetchCampaignsData>>([
        'campaigns',
        userId,
        tenantId
      ]);
      if (!data?.preferences) {
        return;
      }

      // Batch update all campaigns to false
      const resetPromises = Object.keys(data.preferences.skillLevels).map(campaignNumber =>
        preferencesAPI.updateCampaignMobileServicingEligibility(userId, campaignNumber, false)
      );
      await Promise.all(resetPromises);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['campaigns', userId, tenantId] });

      const previousData = queryClient.getQueryData<ReturnType<typeof fetchCampaignsData>>([
        'campaigns',
        userId,
        tenantId
      ]);

      if (previousData?.preferences) {
        const resetMobileEligibility: Record<string, boolean> = {};
        Object.keys(previousData.preferences.skillLevels).forEach(campaignNumber => {
          resetMobileEligibility[campaignNumber] = false;
        });

        queryClient.setQueryData(['campaigns', userId, tenantId], {
          ...previousData,
          preferences: {
            ...previousData.preferences,
            mobileEligibility: resetMobileEligibility
          }
        });
      }

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['campaigns', userId, tenantId], context.previousData);
      }
    },
    onSuccess: () => {
      const metadata = { bulk_reset: true };
      logUserActivity({
        category: 'campaign_config',
        action: 'reset_mobile_eligibility',
        entityType: 'campaign',
        metadata: {
          ...metadata,
          human_description: generateHumanDescription(
            'campaign_config',
            'reset_mobile_eligibility',
            metadata
          )
        }
      });
      queryClient.invalidateQueries({ queryKey: ['campaigns', userId, tenantId] });
    }
  });
}

/**
 * Mutation hook for syncing campaigns from materialized view
 * This syncs unique campaigns from prod.cars_with_safety_recalls_mv to prod.campaigns
 */
export function useSyncCampaigns(userId?: string) {
  const queryClient = useQueryClient();
  const tenantId = useCurrentTenantId();

  return useMutation({
    mutationFn: async () => {
      return await campaignAPI.syncCampaigns();
    },
    onSuccess: () => {
      const metadata = {};
      logUserActivity({
        category: 'campaign_config',
        action: 'sync_campaigns',
        entityType: 'campaign',
        metadata: {
          human_description: generateHumanDescription('campaign_config', 'sync_campaigns', metadata)
        }
      });
      // Invalidate campaigns query to refetch with new data
      queryClient.invalidateQueries({ queryKey: ['campaigns', userId, tenantId] });

      // Also invalidate any related queries
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    }
  });
}
